import { useEffect, useLayoutEffect, useRef } from "react";

export type ShortcutDef = {
  /**
   * Key combination to match. Formats:
   *   "n"           → case-insensitive e.key match (fires on n and N)
   *   "Escape"      → exact e.key match
   *   "?"           → exact e.key match
   *   "+="          → e.key === "+" OR e.key === "="
   *   "/"           → exact e.key match, and only with no modifier
   *   "Alt+S"       → e.altKey + e.code === "KeyS" (macOS-safe; Option+S
   *                   emits "ß" as e.key, so we match via e.code instead)
   *   "CmdOrCtrl+/" → (e.metaKey || e.ctrlKey) + e.key === "/"
   *
   * Single-letter specs have no modifier requirement — pressing Cmd+N while
   * spec is "n" will NOT fire, because the presence of metaKey/ctrlKey/altKey
   * is treated as a mismatch when the spec declares no modifier.
   */
  key: string;
  /**
   * Optional guard: if provided and returns false, the shortcut is skipped.
   * Evaluated after the key matches, before calling run. Use this for view
   * scoping ("only on board"), dialog-state checks, or feature flags.
   */
  when?: () => boolean;
  /** The action to perform. Called after e.preventDefault(). */
  run: () => void;
  /**
   * When true, the shortcut fires even while an <input>, <textarea>, or
   * contenteditable element has keyboard focus. Default: false.
   */
  allowInInput?: boolean;
};

/**
 * Returns true when the KeyboardEvent matches the given key spec string.
 * See ShortcutDef.key for the format.
 */
function matchesKeySpec(spec: string, e: KeyboardEvent): boolean {
  let rest = spec;
  let needsAlt = false;
  let needsCmdOrCtrl = false;

  if (rest.startsWith("Alt+")) {
    needsAlt = true;
    rest = rest.slice(4);
  } else if (rest.startsWith("CmdOrCtrl+")) {
    needsCmdOrCtrl = true;
    rest = rest.slice(10);
  }

  // Modifier mismatch checks
  if (needsAlt !== e.altKey) return false;
  if (needsCmdOrCtrl && !(e.metaKey || e.ctrlKey)) return false;
  if (!needsCmdOrCtrl && (e.metaKey || e.ctrlKey)) return false;

  // Special alias: "+=" matches the plus or equals key
  if (rest === "+=") return e.key === "+" || e.key === "=";

  // Alt + single letter → match via e.code so macOS Option key mapping
  // ("ƒ", "ß", etc.) doesn't prevent the shortcut from firing.
  if (needsAlt && /^[A-Za-z]$/.test(rest)) {
    return e.code === `Key${rest.toUpperCase()}`;
  }

  // Single letter without modifier → case-insensitive e.key match
  if (/^[A-Za-z]$/.test(rest)) {
    return e.key.toLowerCase() === rest.toLowerCase();
  }

  // All other specs (Escape, ?, 1, 2, /, ArrowLeft, …) → exact e.key match
  return e.key === rest;
}

function isInInputField(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement;
  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable
  );
}

/**
 * Attaches a single global `keydown` listener that dispatches to the first
 * matching ShortcutDef in the provided array. The array is captured in a
 * ref so shortcuts always close over fresh state without re-registering the
 * listener on every render.
 *
 * Order matters: put higher-priority or more-specific shortcuts first.
 */
export function useKeyboardShortcuts(shortcuts: ShortcutDef[]): void {
  const shortcutsRef = useRef(shortcuts);
  // Sync the ref in a layout effect (not a passive one) so it updates
  // synchronously after the DOM commit, before paint. With a passive useEffect
  // there's a frame after a render where the DOM already reflects new state
  // (e.g. a freshly rendered filter control or the emails-view button after
  // Gmail connects) but the ref still holds the previous render's shortcuts — a
  // keydown in that window dispatches against stale shortcuts and is dropped.
  // Running before paint closes the window: once the UI is observable, the
  // handler already sees the current shortcuts.
  useLayoutEffect(() => {
    shortcutsRef.current = shortcuts;
  });

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      for (const def of shortcutsRef.current) {
        if (!matchesKeySpec(def.key, e)) continue;
        if (!def.allowInInput && isInInputField(e)) continue;
        if (def.when && !def.when()) continue;
        e.preventDefault();
        def.run();
        return;
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);
}
